import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/lib/assets";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 1, 2026" testId="privacy-page">
      <p className="mt-8 text-[15px] leading-relaxed text-[var(--ink-soft)]">
        This Privacy Policy explains how Domaya ("Domaya", "we", "us" or "our")
        collects, uses and protects your information when you use the Domaya
        mobile application and the website domaya.app (together, the
        "Service"). By using the Service you agree to the practices described
        in this policy. If you do not agree, please do not use the Service.
      </p>

      <LegalSection title="1. Information We Collect">
        <p>
          <strong className="text-[var(--ink)]">Account information.</strong> When
          you create an account we collect your name, email address and
          password (stored in hashed form), or your Google account profile
          information if you choose to sign in with Google.
        </p>
        <p>
          <strong className="text-[var(--ink)]">Household content.</strong> Information
          you add to the app, such as pantry items, shopping lists, routines,
          care notes, maintenance tasks, reminders, appliances, warranties and
          household member details.
        </p>
        <p>
          <strong className="text-[var(--ink)]">Photos and camera access.</strong> If
          you use the AI pantry scanning feature, the app requests access to
          your camera or photo library. Images you capture or upload are used
          solely to identify pantry items and are processed as described in
          Section 3. Camera access is optional and only used when you actively
          choose to scan.
        </p>
        <p>
          <strong className="text-[var(--ink)]">Payment information.</strong> Subscriptions
          are processed by Stripe and/or the Apple App Store and Google Play.
          We do not store your full card details; we receive only your
          subscription status and limited billing metadata.
        </p>
        <p>
          <strong className="text-[var(--ink)]">Device and usage data.</strong> We
          collect limited technical data such as device type, operating
          system, app version and crash logs to keep the Service reliable and
          secure.
        </p>
      </LegalSection>

      <LegalSection title="2. How We Use Your Information">
        <p>We use your information to:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>provide, maintain and improve the Service;</li>
          <li>identify pantry items from photos you choose to scan;</li>
          <li>send reminders, notifications and service emails (via Resend);</li>
          <li>manage subscriptions and verify subscription status (via Stripe, the App Store or Google Play);</li>
          <li>sync your household data across your devices and household members you invite;</li>
          <li>respond to support requests; and</li>
          <li>protect the security and integrity of the Service and comply with the law.</li>
        </ul>
        <p>
          We do not sell your personal information, and we do not use your
          household content for advertising.
        </p>
      </LegalSection>

      <LegalSection title="3. AI Features">
        <p>
          When you use AI features such as pantry scanning or recipe
          suggestions, the photos or relevant pantry data you provide are sent
          to our AI processing providers solely to generate the requested
          result (for example, a list of recognised items or a recipe idea).
          These providers process the data on our behalf and are not permitted
          to use it for their own purposes. AI output can be inaccurate;
          always use your own judgment, particularly with food safety and
          expiry dates.
        </p>
      </LegalSection>

      <LegalSection title="4. Google User Data">
        <p>
          If you choose to sign in with Google or connect Google Calendar, we
          access only the data needed to provide those features: your basic
          profile (name, email, profile picture) for sign-in, and calendar
          access only to create or read the events you ask Domaya to manage.
        </p>
        <p>
          Domaya's use and transfer of information received from Google APIs
          adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--olive)] underline"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements. We never use Google user
          data for advertising, and we do not allow humans to read this data
          except with your consent, for security purposes, or as required by
          law. You can revoke Domaya's access at any time via your{" "}
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--olive)] underline"
          >
            Google Account permissions
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="5. How We Share Information">
        <p>
          We share information only with service providers who help us run the
          Service, under contracts that protect your data:
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>cloud hosting and database providers (to store your data securely);</li>
          <li>AI processing providers (to power pantry scanning and suggestions);</li>
          <li>Stripe (subscription billing) and the app stores (purchases);</li>
          <li>Resend (transactional email delivery); and</li>
          <li>analytics/crash reporting limited to app stability.</li>
        </ul>
        <p>
          Household content you add is visible to other members of your
          household that you invite. We may also disclose information if
          required by law, or to protect the rights, safety and security of
          Domaya, our users or the public.
        </p>
      </LegalSection>

      <LegalSection title="6. Data Retention and Deletion">
        <p>
          We keep your information for as long as your account is active. You
          may delete your account at any time from within the app (Settings →
          Account → Delete account) or by emailing us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--olive)] underline">
            {CONTACT_EMAIL}
          </a>
          . When you delete your account, your personal data and household
          content are permanently deleted from our production systems within
          30 days, except where we must retain limited records to comply with
          legal obligations.
        </p>
      </LegalSection>

      <LegalSection title="7. Security">
        <p>
          We use industry-standard safeguards, including encryption in transit
          (HTTPS/TLS), hashed passwords and access controls, to protect your
          information. No method of transmission or storage is completely
          secure, so we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="8. Children's Privacy">
        <p>
          The Service is not directed to children under 13 (or the equivalent
          minimum age in your jurisdiction), and we do not knowingly collect
          personal information from them. If you believe a child has provided
          us personal information, contact us and we will delete it.
        </p>
      </LegalSection>

      <LegalSection title="9. Your Rights">
        <p>
          Depending on where you live, you may have rights to access, correct,
          export, restrict or delete your personal information, and to object
          to certain processing. You can exercise most of these directly in
          the app, or by contacting us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--olive)] underline">
            {CONTACT_EMAIL}
          </a>
          . We will respond within the timeframe required by applicable law.
          If you are in the EEA/UK you may also lodge a complaint with your
          local supervisory authority.
        </p>
      </LegalSection>

      <LegalSection title="10. International Transfers">
        <p>
          Your information may be processed in countries other than your own.
          Where required, we use appropriate safeguards (such as standard
          contractual clauses) to protect data transferred internationally.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. If we make
          material changes we will notify you through the app or by email
          before the changes take effect. The "Last updated" date at the top
          shows the latest revision.
        </p>
      </LegalSection>

      <LegalSection title="12. Contact Us">
        <p>
          Questions about this policy or your data? Contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--olive)] underline" data-testid="privacy-contact-email">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
